import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { initializeOpenAPI } from '../config/openapi';
import PrivateRoute from '../components/PrivateRoute';
import PublicRoute from '../components/PublicRoute';
import LoadingScreen from '../components/LoadingScreen';
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import DashboardPage from '../pages/DashboardPage';
import TasksPage from '../pages/TasksPage';
import ProfilePage from '../pages/ProfilePage';
import DashboardLayout from '../components/layout/DashboardLayout';
import LicenseBuyPage from '../pages/LicenseBuyPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import ActivateAccountPage from '../pages/auth/ActivateAccountPage';
import VerifyEmailPage from '../pages/auth/VerifyEmailPage';
import FreshdeskPage from '../pages/FreshdeskPage';
import GpsrPage from '../pages/GpsrPage';
import FormQuestionsPage from '../pages/FormQuestionsPage';
import LieferbarPage from '../pages/LieferbarPage';
import MobileAppLicensePage from '../pages/MobileAppLicensePage';
import FeaturesPage from '../pages/FeaturesPage';
import ConnectorsPage from '../pages/ConnectorsPage';
import ContentMasterPage from '../pages/ContentMasterPage';
import ContentMasterBulkReviewPage from '../pages/ContentMasterBulkReviewPage';
import MarketplaceListingReviewPage from '../pages/MarketplaceListingReviewPage';
import ContentMasterProductPage from '../pages/ContentMasterProductPage';
import ContentMasterUsagePage from '../pages/ContentMasterUsagePage';
import ContentMasterGuard from '../components/ContentMasterGuard';
import PermissionGuard from '../components/PermissionGuard';
import ShipmentTrackerGuard from '../components/ShipmentTrackerGuard';
import GoogleReviewGuard from '../components/GoogleReviewGuard';
import GoogleReviewManagerPage from '../pages/GoogleReviewManagerPage';
import GoogleReviewSettingsPage from '../pages/GoogleReviewSettingsPage';
import GoogleReviewBulkReviewPage from '../pages/GoogleReviewBulkReviewPage';
import ShipmentTrackerDashboardPage from '../pages/shipment-tracker/ShipmentTrackerDashboardPage';
import ShipmentTrackerShipmentsPage from '../pages/shipment-tracker/ShipmentTrackerShipmentsPage';
import ShipmentTrackerCreatePage from '../pages/shipment-tracker/ShipmentTrackerCreatePage';
import ShipmentTrackerDetailPage from '../pages/shipment-tracker/ShipmentTrackerDetailPage';
import ShipmentTrackerStatusBoardPage from '../pages/shipment-tracker/ShipmentTrackerStatusBoardPage';
import ShipmentTrackerArchivesPage from '../pages/shipment-tracker/ShipmentTrackerArchivesPage';
import ShipmentTrackerSettingsPage from '../pages/shipment-tracker/ShipmentTrackerSettingsPage';
import ShipmentItemFormPage from '../pages/shipment-tracker/ShipmentItemFormPage';
import PartnerApplyPage from '../pages/PartnerApplyPage';
import PartnerDashboardPage from '../pages/PartnerDashboardPage';

// Component for handling logout process
const LogoutHandler: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const handleLogout = async () => {
      try {
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Reset OpenAPI
        initializeOpenAPI(undefined);

        // Dispatch logout action
        dispatch(logout());

        // Navigate to login
        navigate('/login');
      } catch (error) {
        console.error('Logout error:', error);
        navigate('/login');
      }
    };

    handleLogout();
  }, [dispatch, navigate]);

  return <LoadingScreen />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/activate-account"
        element={
          <PublicRoute>
            <ActivateAccountPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <VerifyEmailPage />
        }
      />
      {/* Public partner application page - accessible with or without auth
          (no PublicRoute wrapper: logged-in users must not be redirected). */}
      <Route
        path="/partner"
        element={
          <PartnerApplyPage />
        }
      />
      <Route element={<DashboardLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <PermissionGuard permission="mobile_read">
                <DashboardPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <PrivateRoute>
              <PermissionGuard permission="mobile_read">
                <TasksPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features"
          element={
            <PrivateRoute>
              <FeaturesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/connectors"
          element={
            <PrivateRoute>
              <ConnectorsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/invoices"
          element={<Navigate to="/profile?direction=invoices" replace />}
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/licenses"
          element={<Navigate to="/profile?direction=licenses" replace />}
        />
        <Route
          path="/licenses/buy"
          element={
            <PrivateRoute>
              <LicenseBuyPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/features/freshdesk"
          element={
            <PrivateRoute>
              <PermissionGuard permission="freshdesk_read">
                <FreshdeskPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/logout"
          element={
            <PrivateRoute>
              <LogoutHandler />
            </PrivateRoute>
          }
        />
        <Route
          path="/features/gpsr"
          element={
            <PrivateRoute>
              <PermissionGuard permission="gpsr_read">
                <GpsrPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/gpsr/product/add"
          element={
            <PrivateRoute>
              <PermissionGuard permission="gpsr_write">
                <FormQuestionsPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/gpsr/product/edit/:productID"
          element={
            <PrivateRoute>
              <PermissionGuard permission="gpsr_write">
                <FormQuestionsPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/content-master/usage"
          element={
            <PrivateRoute>
              <ContentMasterGuard>
                <ContentMasterUsagePage />
              </ContentMasterGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/content-master/bulk-review"
          element={
            <PrivateRoute>
              <ContentMasterGuard>
                <ContentMasterBulkReviewPage />
              </ContentMasterGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/content-master/marketplace-listing"
          element={
            <PrivateRoute>
              <ContentMasterGuard>
                <MarketplaceListingReviewPage />
              </ContentMasterGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/content-master/product/:productId"
          element={
            <PrivateRoute>
              <ContentMasterGuard>
                <ContentMasterProductPage />
              </ContentMasterGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/content-master"
          element={
            <PrivateRoute>
              <ContentMasterGuard>
                <ContentMasterPage />
              </ContentMasterGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/mobile-app"
          element={
            <PrivateRoute>
              <MobileAppLicensePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/features/google-review-manager/bulk-review"
          element={
            <PrivateRoute>
              <GoogleReviewGuard>
                <GoogleReviewBulkReviewPage />
              </GoogleReviewGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/google-review-manager/settings"
          element={
            <PrivateRoute>
              <GoogleReviewGuard>
                <GoogleReviewSettingsPage />
              </GoogleReviewGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/google-review-manager"
          element={
            <PrivateRoute>
              <GoogleReviewGuard>
                <GoogleReviewManagerPage />
              </GoogleReviewGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/lieferbar"
          element={
            <PrivateRoute>
              <PermissionGuard permission="mobile_read">
                <LieferbarPage />
              </PermissionGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerDashboardPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/shipments"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerShipmentsPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/shipments/new"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerCreatePage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/shipments/:shipmentId/edit"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerCreatePage isEditing />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/shipments/:shipmentId"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerDetailPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/status-board"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerStatusBoardPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/settings"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerSettingsPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/archives"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentTrackerArchivesPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        {/* Item form - new shipment (items staged locally until first save) */}
        <Route
          path="/features/shipment-tracker/shipments/new/items/add"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentItemFormPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        {/* Item form - saved shipment */}
        <Route
          path="/features/shipment-tracker/shipments/:shipmentId/items/add"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentItemFormPage />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/features/shipment-tracker/shipments/:shipmentId/items/:itemId/edit"
          element={
            <PrivateRoute>
              <ShipmentTrackerGuard>
                <ShipmentItemFormPage isEditing />
              </ShipmentTrackerGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/user-management"
          element={<Navigate to="/profile?direction=users" replace />}
        />
        <Route
          path="/partner-dashboard"
          element={
            <PrivateRoute>
              <PartnerDashboardPage />
            </PrivateRoute>
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
